import { useEffect, useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import { fr, enGB, de, it } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang, useT } from "@/i18n";

const LOCALES = { fr, en: enGB, de, it };
export const toIso = (d) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "");
export const fromIso = (s) => (s ? new Date(`${s}T12:00:00`) : undefined);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export function minNightsFor(day, rules) {
  if (!rules || !day) return 1;
  return (rules.winter_months || []).includes(day.getMonth() + 1) ? rules.min_nights_winter || 1 : rules.min_nights_summer || 1;
}

function useIsMobile() {
  const [m, setM] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => { const h = () => setM(window.innerWidth < 768); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, []);
  return m;
}

/**
 * Sélecteur de séjour (arrivée -> départ) : nuits occupées non sélectionnables, jour de départ d'un séjour
 * existant disponible pour une arrivée, séjour minimum appliqué selon le mois d'arrivée.
 * value = { start: "AAAA-MM-JJ", end: "AAAA-MM-JJ" } (end = jour du départ).
 */
// prices : { "AAAA-MM-JJ": prix par nuit } — si fourni (grille tarifaire renseignée), le prix s'affiche sous chaque jour
// et les nuits sans tarif ne sont pas réservables (« - »).
export default function StayPicker({ busy = [], rules, value, onChange, testId = "stay-picker", prices = null }) {
  const t = useT();
  const lang = useLang();
  const mobile = useIsMobile();
  const langIntl = { fr: "fr-FR", en: "en-GB", de: "de-DE", it: "it-IT" }[lang] || "fr-FR";
  const today = useMemo(() => { const d = new Date(); d.setHours(12, 0, 0, 0); return d; }, []);
  const busyNights = useMemo(() => {
    const set = new Set();
    for (const b of busy) for (let d = fromIso(b.start); toIso(d) < b.end; d = addDays(d, 1)) set.add(toIso(d));
    return set;
  }, [busy]);
  const from = fromIso(value?.start);
  const to = fromIso(value?.end);
  const choosingEnd = !!from && !to;
  // mois affiché contrôlé : suit la date d'arrivée même si elle est renseignée après le premier rendu
  const [month, setMonth] = useState(() => (from && from > today ? from : today));
  useEffect(() => {
    if (!value?.start) return;
    const d = fromIso(value.start);
    setMonth((m) => {
      const shown = (y, mo) => y * 12 + mo;
      const diff = shown(d.getFullYear(), d.getMonth()) - shown(m.getFullYear(), m.getMonth());
      return diff < 0 || diff > 1 ? new Date(d.getFullYear(), d.getMonth(), 1) : m;
    });
  }, [value?.start]);

  const priced = !!prices && Object.keys(prices).length > 0;
  const nightBusy = (d) => busyNights.has(toIso(d)) || (priced && prices[toIso(d)] == null);
  const rangeFree = (a, b) => { for (let d = new Date(a); d < b; d = addDays(d, 1)) if (nightBusy(d)) return false; return true; };
  const minN = minNightsFor(from, rules);

  const disabled = (day) => {
    if (toIso(day) < toIso(today)) return true;
    if (choosingEnd && day > from) {
      const nights = Math.round((day - from) / 86400000);
      return nights < minN || !rangeFree(from, day);
    }
    return nightBusy(day); // arrivée impossible sur une nuit occupée
  };

  const click = (day, mods) => {
    if (mods.disabled) return;
    if (choosingEnd && day > from) return onChange({ start: value.start, end: toIso(day) });
    onChange({ start: toIso(day), end: "" });
  };

  const nights = from && to ? Math.round((to - from) / 86400000) : 0;
  return (
    <div data-testid={testId}>
      <DayPicker
        mode="range" month={month} onMonthChange={setMonth} selected={{ from, to }} onSelect={() => {}} onDayClick={click} disabled={disabled}
        locale={LOCALES[lang] || fr} weekStartsOn={1} numberOfMonths={mobile ? 1 : 2} fromMonth={today}
        showOutsideDays={false} modifiers={{ busy: nightBusy }}
        modifiersClassNames={{ busy: "stay-busy" }}
        components={{
          IconLeft: () => <ChevronLeft size={16} />, IconRight: () => <ChevronRight size={16} />,
          ...(prices ? { DayContent: ({ date: d }) => {
            const p = prices[toIso(d)];
            return (
              <span className="flex flex-col items-center leading-none" data-testid={`${testId}-day-${toIso(d)}`}>
                <span>{d.getDate()}</span>
                <span className={`text-[10px] mt-1 ${p != null ? "opacity-70" : "opacity-40"}`} data-testid={`${testId}-price-${toIso(d)}`}>{p != null ? `${Math.round(p)} €` : "-"}</span>
              </span>
            );
          } } : {}),
        }}
        classNames={{
          months: "flex flex-col md:flex-row gap-6", month: "space-y-3 w-full", caption: "flex justify-center pt-1 relative items-center",
          caption_label: "font-serif text-lg text-forest capitalize", nav: "flex items-center",
          nav_button: "h-8 w-8 inline-flex items-center justify-center rounded-full border border-sand bg-white text-forest hover:border-forest disabled:opacity-30",
          nav_button_previous: "absolute left-0", nav_button_next: "absolute right-0", table: "w-full border-collapse",
          head_row: "flex", head_cell: "text-charcoal/40 w-full text-[11px] uppercase font-medium", row: "flex w-full mt-1",
          cell: "w-full text-center text-sm p-0 relative", day: `${prices ? "h-12" : "h-10"} w-full rounded-lg text-sm text-charcoal hover:bg-sand/60 transition aria-selected:opacity-100`,
          day_selected: "!bg-forest !text-ecru hover:bg-forest", day_range_middle: "!bg-sage/25 !text-forest rounded-none",
          day_range_start: "rounded-r-none", day_range_end: "rounded-l-none", day_today: "font-semibold underline decoration-gold underline-offset-4",
          day_disabled: "text-charcoal/25 hover:bg-transparent cursor-not-allowed", day_outside: "invisible",
        }}
      />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-charcoal/60">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded stay-busy-swatch" /> {t("Occupé")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-forest" /> {t("Votre séjour")}</span>
        {rules && rules.min_nights_winter !== rules.min_nights_summer && <span>{t("Séjour minimum : {w} nuits pour une arrivée en {months}, {s} nuit(s) le reste de l'année", { w: rules.min_nights_winter, s: rules.min_nights_summer, months: (rules.winter_months || []).slice().sort((a, b) => ((a + 2) % 12) - ((b + 2) % 12)).map((m) => new Date(2026, m - 1, 1).toLocaleDateString(langIntl, { month: "long" })).join(", ") })}</span>}
      </div>
      <p className="text-sm text-forest mt-2 min-h-[20px]" data-testid={`${testId}-summary`}>
        {choosingEnd ? t("Choisissez maintenant votre date de départ (minimum {n} nuits).", { n: minN })
          : nights ? t("{n} nuit(s) sélectionnée(s)", { n: nights }) : t("Choisissez votre date d'arrivée.")}
      </p>
    </div>
  );
}
